import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ChangeReasonCode,
  type EventRevisionDto,
  type EventRevisionPatchDto,
  type SourcingEventV2Dto,
  useCancelEventChangeMutation,
  useGetSourcingEventQuery,
  useSubmitEventChangeMutation,
  useUpdateEventChangeMutation,
} from "../../../../autogen/bff-api";
import { DropTarget } from "../../../../common/documents/UppyUploader/DropTarget";
import {
  FileRow,
  type IncompleteFileUpload,
  isIncompleteFileUpload,
} from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import {
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
} from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { ThemedDateTimePicker } from "../../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { deadlineEditingOf } from "./changeWindow";

interface Props {
  event: SourcingEventV2Dto;
  draft: EventRevisionDto;
  isOpen: boolean;
  onClose: () => void;
}

export const EventChangeEditor = ({ event, draft, isOpen, onClose }: Props) => {
  const [update] = useUpdateEventChangeMutation();
  const editorErrorConfig = useApiErrorEditorConfig();

  useCreateEditor<EventRevisionDto, EventRevisionPatchDto>(draft.id, {
    initialState: draft,
    save: (patch) =>
      update({ eventId: event.id, revisionId: draft.id, eventRevisionPatchDto: patch }).unwrap(),
    ...editorErrorConfig,
  });

  return <EventChangeEditorBody event={event} draft={draft} isOpen={isOpen} onClose={onClose} />;
};

const EventChangeEditorBody = ({ event, draft, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const { state, updateField, fieldStatus, flushPending } =
    useEditor<EventRevisionDto, EventRevisionPatchDto>(draft.id);
  const [submit, { isLoading: isSubmitting }] = useSubmitEventChangeMutation();
  const [cancel, { isLoading: isCanceling }] = useCancelEventChangeMutation();
  const { refetch } = useGetSourcingEventQuery({ eventId: event.id });

  const { uppy, allFiles, removeUpload } = useUppyUploader({
    entityType: "SourcingEventV2",
    entityId: event.id,
    reloadEntity: refetch,
  });
  const pendingUploads = allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[];

  const privateContext = event.eventContext.type === "Private" ? event.eventContext : null;
  const recipientCount = privateContext
    ? privateContext.organizationInvites.length
    : event.supplierRegistrations?.length ?? 0;
  const deadline = deadlineEditingOf(event);
  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";

  const eventDocuments = event.eventData.documents ?? [];
  const includedDocumentIds = state.requestDocumentIds ?? [];

  // Documents uploaded while the editor is open join the change automatically; documents that
  // existed when it opened keep whatever inclusion the draft was forked with.
  const seenDocumentIdsRef = useRef<Set<string>>(new Set(eventDocuments.map((doc) => doc.id)));
  useEffect(() => {
    const fresh = eventDocuments.filter((doc) => !seenDocumentIdsRef.current.has(doc.id));
    if (fresh.length === 0) return;
    fresh.forEach((doc) => seenDocumentIdsRef.current.add(doc.id));
    updateField({ requestDocumentIds: [...includedDocumentIds, ...fresh.map((doc) => doc.id)] });
  }, [eventDocuments, includedDocumentIds, updateField]);

  const toggleDocument = (documentId: string, include: boolean) => {
    const next = include
      ? [...includedDocumentIds, documentId]
      : includedDocumentIds.filter((id) => id !== documentId);
    updateField({ requestDocumentIds: next });
  };

  const handleSubmit = async () => {
    try {
      await flushPending();
      await submit({ eventId: event.id, revisionId: draft.id }).unwrap();
      toast({
        title: privateContext ? t("The change has been published") : t("The change notice has been submitted"),
        status: "success",
      });
      onClose();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const handleCancel = async () => {
    try {
      await cancel({ eventId: event.id, revisionId: draft.id }).unwrap();
      onClose();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{privateContext ? t("Change the request") : t("Change the announcement")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="5">
            <FormControl>
              <FormLabel>
                {t("New bid deadline")} {isSaving("bidSubmissionDeadline") && <Spinner size="xs" />}
              </FormLabel>
              <ThemedDateTimePicker
                date={state.bidSubmissionDeadline}
                onChange={(iso) => updateField({ bidSubmissionDeadline: iso ?? null })}
                minDate={deadline.earliestSelectable}
              />
              <FormHelperText>
                {deadline.canReopen
                  ? t("The bid deadline has passed. Pick a new one ahead in time to reopen the competition.")
                  : t("The bid deadline can only be extended, never shortened")}
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>
                {t("Description")} {isSaving("description") && <Spinner size="xs" />}
              </FormLabel>
              <TipTap
                content={state.description ?? ""}
                onUpdate={async (value) => updateField({ description: value })}
                editable={true}
                debounceWait={300}
              />
            </FormControl>

            <FormControl>
              <FormLabel>
                {t("Competition documents")} {isSaving("requestDocumentIds") && <Spinner size="xs" />}
              </FormLabel>
              {eventDocuments.length > 0 && (
                <Stack spacing="1" mb="3">
                  {eventDocuments.map((doc) => (
                    <Checkbox
                      key={doc.id}
                      isChecked={includedDocumentIds.includes(doc.id)}
                      onChange={(e) => toggleDocument(doc.id, e.target.checked)}
                    >
                      {doc.name}
                    </Checkbox>
                  ))}
                </Stack>
              )}
              {pendingUploads.map((upload) => (
                <FileRow key={upload.uploadId} file={upload} name={upload.name} remove={removeUpload} />
              ))}
              <DropTarget
                targetId={`event-change-${draft.id}`}
                uppy={uppy}
                helperText={t("Drag and drop files here, or click to browse")}
              />
              <FormHelperText>
                {t("Checked documents are part of the announcement; new uploads are included automatically")}
              </FormHelperText>
            </FormControl>

            {!privateContext && (
              <FormControl>
                <FormLabel>{t("Reason for the change")}</FormLabel>
                <RadioGroup
                  value={state.reasonCode ?? ""}
                  onChange={(value) => updateField({ reasonCode: value as ChangeReasonCode })}
                >
                  <Stack direction="row" spacing="6">
                    <Radio value={ChangeReasonCode.UpdateAdd}>{t("Updated information")}</Radio>
                    <Radio value={ChangeReasonCode.CorrectionBuyer}>{t("Correction")}</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            )}

            <FormControl>
              <FormLabel>
                {t("What changed?")} {isSaving("changeDescription") && <Spinner size="xs" />}
              </FormLabel>
              <Textarea
                value={state.changeDescription ?? ""}
                onChange={(e) => updateField({ changeDescription: e.target.value }, { debounceMs: 300 })}
                placeholder={t("Describe the change for the suppliers") ?? undefined}
              />
              <FormHelperText>{t("Shown to suppliers and published with the change notice")}</FormHelperText>
            </FormControl>

            {deadline.canReopen && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t(
                  "The competition is closed for bids right now. Publishing the change reopens it until the new deadline you pick.",
                )}
              </Alert>
            )}

            {deadline.isClosed && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t(
                  "The bid deadline has passed, so this change can no longer be published. An announced competition closes at its deadline.",
                )}
              </Alert>
            )}

            {deadline.warnsDeadlineIsNear && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t(
                  "The current deadline is less than 24 hours away. The change takes effect only once Doffin confirms it.",
                )}
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Flex justify="space-between" align="center" gap="4" w="full" wrap="wrap">
            <Text fontSize="sm" color="smMuted">
              {privateContext
                ? t("{{count}} invited suppliers will be notified", { count: recipientCount })
                : t("{{count}} registered suppliers will be notified", { count: recipientCount })}
            </Text>
            <Flex gap="3">
              <Button variant="ghost" onClick={handleCancel} isLoading={isCanceling}>
                {t("Discard change")}
              </Button>
              <Button
                colorScheme="teal"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={deadline.isClosed}
              >
                {privateContext ? t("Publish the change") : t("Submit change notice")}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
